import React, { useEffect, useMemo, useState } from "react";
import { IconChevronDown, IconFileText, IconSearch, IconX } from "@tabler/icons-react";
import {
  useEncoderCatalog,
  useEncoderCoverage,
  useEncoderCoverageStates,
  useEncoderLcdDocument,
} from "../../hooks/queries";
import type {
  CodeSearchResult,
  CoverageArticle,
  CoverageCode,
  CoverageCodeGroup,
} from "../../types/encoder";
import { cn, encoderErrorMessage } from "../../utils/encoder";
import { sanitizeGuidelineHtml } from "../../utils/guideline-markdown";
import {
  CodeSearchCombobox,
  EncoderBrandEmpty,
  NativeSelect,
  Spinner,
} from "./shared";

const SECTION_KEYS = [
  { id: "contractor-information", label: "Contractor Information" },
  { id: "article-information", label: "Article Information" },
  { id: "cpt-hcpcs", label: "CPT/HCPCS" },
  { id: "cpt-hcpcs-modifiers", label: "CPT/HCPCS Modifiers" },
  { id: "icd-covered", label: "ICD-10 Covered" },
  { id: "icd-noncovered", label: "ICD-10 Noncovered" },
  { id: "icd-pcs", label: "ICD-10-PCS" },
] as const;

const CodeTable: React.FC<{ codes?: CoverageCode[] }> = ({ codes }) => {
  if (!codes?.length) return <p className="text-sm text-slate-500">None listed.</p>;
  return (
    <table className="mt-2 min-w-full border-y-2 border-slate-700 text-left text-sm">
      <thead>
        <tr>
          <th className="w-40 border-r px-2 py-2 font-semibold">Code</th>
          <th className="px-2 py-2 font-semibold">Description</th>
        </tr>
      </thead>
      <tbody>
        {codes.map((code) => (
          <tr key={`${code.code}-${code.description}`} className="border-t">
            <td className="border-r px-2 py-1 font-mono">{code.code}</td>
            <td className="px-2 py-1">{code.description || code.shortDescription}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const CodeGroups: React.FC<{ groups?: CoverageCodeGroup[] }> = ({ groups }) => {
  if (!groups?.length) return <p className="text-sm text-slate-500">None listed.</p>;
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.group}>
          {group.paragraphHtml && (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: sanitizeGuidelineHtml(group.paragraphHtml),
              }}
            />
          )}
          <CodeTable codes={group.codes} />
        </div>
      ))}
    </div>
  );
};

const articleContainsDiagnosis = (article: CoverageArticle, query: string) =>
  (article.icd10Covered ?? []).some((group) =>
    group.codes.some((code) =>
      code.code.toLowerCase().replaceAll(".", "").includes(query)
    )
  );

const ArticleSection: React.FC<{
  article: CoverageArticle;
  section: (typeof SECTION_KEYS)[number]["id"];
  onOpenLcd: (lcdId: string) => void;
}> = ({ article, section, onOpenLcd }) => {
  if (section === "contractor-information") {
    return (
      <div className="space-y-2 text-sm">
        {(article.contractors ?? []).map((contractor, index) => (
          <div key={`${contractor.contractorId ?? index}`} className="rounded border p-2">
            <p className="font-semibold">{contractor.name}</p>
            <p>{contractor.contractNumber} {contractor.contractType}</p>
            <p>
              {(contractor.states ?? [])
                .map((state) => state.abbreviation)
                .join(", ")}
            </p>
          </div>
        ))}
      </div>
    );
  }
  if (section === "article-information") {
    const dates = article.articleStatusDates;
    return (
      <div className="space-y-2 text-sm">
        <p>
          <span className="font-semibold">Status:</span> {dates?.status || "—"}
        </p>
        <p>
          <span className="font-semibold">Type:</span> {dates?.articleType || "—"}
        </p>
        <p>
          <span className="font-semibold">Effective:</span>{" "}
          {dates?.effectiveDate || "—"}
        </p>
        <p>
          <span className="font-semibold">Publication:</span>{" "}
          {dates?.publicationDate || "—"}
        </p>
        {(article.lcdReferences ?? []).map((lcd) => (
          <button
            key={lcd.lcdId}
            type="button"
            className="block text-blue-800 underline"
            onClick={() => onOpenLcd(lcd.lcdId)}
          >
            LCD {lcd.lcdId}
          </button>
        ))}
      </div>
    );
  }
  if (section === "cpt-hcpcs") return <CodeGroups groups={article.cptHcpcsCodes} />;
  if (section === "cpt-hcpcs-modifiers")
    return <CodeGroups groups={article.cptHcpcsModifiers} />;
  if (section === "icd-covered") return <CodeGroups groups={article.icd10Covered} />;
  if (section === "icd-noncovered")
    return <CodeGroups groups={article.icd10Noncovered} />;
  return <CodeGroups groups={article.icd10PcsCodes} />;
};

const McdCoverageTab: React.FC<{ procedure?: string }> = ({ procedure }) => {
  const catalog = useEncoderCatalog();
  const [submittedCode, setSubmittedCode] = useState(procedure ?? "");
  const [selectedCode, setSelectedCode] = useState<CodeSearchResult | null>(null);
  const [state, setState] = useState("All");
  const [diagnosisQuery, setDiagnosisQuery] = useState("");
  const [openArticleId, setOpenArticleId] = useState<string | undefined>();
  const [openSections, setOpenSections] = useState<Record<string, string>>(
    {}
  );
  const statesQuery = useEncoderCoverageStates();
  const coverageQuery = useEncoderCoverage(
    submittedCode,
    state === "All" ? undefined : state
  );
  const lcd = useEncoderLcdDocument();
  const articles = useMemo(
    () => (coverageQuery.data?.articles ?? []) as CoverageArticle[],
    [coverageQuery.data?.articles]
  );
  const diagnosis = diagnosisQuery.trim().toLowerCase().replaceAll(".", "");
  const visible = useMemo(
    () =>
      articles
        .map((article, index) => ({ article, articleNumber: index + 1 }))
        .filter(
          ({ article }) => !diagnosis || articleContainsDiagnosis(article, diagnosis)
        ),
    [articles, diagnosis]
  );

  useEffect(() => {
    if (!procedure) return;
    setSubmittedCode(procedure);
    setSelectedCode(null);
    setOpenArticleId(undefined);
    setDiagnosisQuery("");
  }, [procedure]);

  useEffect(() => {
    if (!articles[0]) return;
    setOpenArticleId((current) => current ?? articles[0].articleId);
  }, [articles]);

  const openLcd = async (lcdId: string) => {
    try {
      const url = await lcd.mutateAsync(lcdId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <div className="border-b p-2">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <CodeSearchCombobox
            year={catalog.data?.codeYears?.[0] ?? 2026}
            type="CPT"
            accent="yellow"
            placeholder="Search CPT/HCPCS code for MCD coverage..."
            displayValue={
              selectedCode
                ? `${selectedCode.code} - ${selectedCode.description}`
                : submittedCode
            }
            onSelect={(result) => {
              setSelectedCode(result);
              setSubmittedCode(result.code);
              setDiagnosisQuery("");
              setOpenArticleId(undefined);
            }}
          />
          <div className="flex shrink-0 items-center gap-4">
            <span className="text-sm font-medium text-slate-500">State:</span>
            <div className="inline-flex h-9 items-center rounded-md bg-gray-300 p-1">
              <NativeSelect
                value={state}
                onChange={setState}
                className="h-7 w-40 border-white bg-green-200 font-semibold text-green-700 shadow-lg"
                options={[
                  { value: "All", label: "All" },
                  ...(statesQuery.data ?? []).map(
                    (item: { abbreviation: string; name: string }) => ({
                      value: item.abbreviation,
                      label: `${item.abbreviation} - ${item.name}`,
                    })
                  ),
                ]}
              />
            </div>
          </div>
          {articles.length > 0 && (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="shrink-0 text-sm text-slate-500">
                Search Dx code within Articles:
              </span>
              <div className="relative min-w-0 flex-1">
                <IconSearch
                  size={14}
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={diagnosisQuery}
                  onChange={(event) => setDiagnosisQuery(event.target.value)}
                  placeholder="Search Dx code..."
                  className="h-9 w-full rounded-md border-2 border-yellow-500 bg-yellow-50 pl-8 pr-16 text-sm"
                />
                {diagnosisQuery && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                    onClick={() => setDiagnosisQuery("")}
                  >
                    <IconX size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {coverageQuery.data && articles.length > 0 && (
        <div className="mx-2 mt-2 rounded border border-[#005ea2]/30 bg-[#eef6ff] p-3 text-sm">
          <p>
            <span className="font-semibold">{submittedCode}</span>
          </p>
          <p className="font-semibold text-red-600">
            * Number of articles associated with this CPT: {articles.length}
          </p>
        </div>
      )}

      <main className="min-h-0 flex-1 overflow-auto p-2">
        {coverageQuery.isError && (
          <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Coverage lookup failed:{" "}
            {encoderErrorMessage(coverageQuery.error, "Failed to fetch coverage data")}
          </p>
        )}
        {coverageQuery.isFetching && !coverageQuery.data && (
          <Spinner label="Loading coverage data..." />
        )}
        {!submittedCode && (
          <EncoderBrandEmpty title="MCD Coverage" className="min-h-[55vh]" />
        )}
        {!coverageQuery.isFetching && submittedCode && articles.length === 0 && (
          <div className="flex min-h-[55vh] flex-col items-center justify-center text-center">
            <IconFileText className="mb-3 text-slate-400" />
            <p className="font-semibold">No coverage articles found</p>
            <p className="text-sm text-slate-500">Try another CPT or HCPCS code.</p>
          </div>
        )}
        {diagnosis && visible.length === 0 && articles.length > 0 && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center">
            <p className="text-xl font-semibold">No articles contain this diagnosis code</p>
            <button
              type="button"
              className="mt-2 text-blue-800 underline"
              onClick={() => setDiagnosisQuery("")}
            >
              Clear diagnosis search
            </button>
          </div>
        )}
        <div className="space-y-4">
          {visible.map(({ article, articleNumber }) => {
            const open = openArticleId === article.articleId;
            return (
              <article key={article.articleId} className="rounded-lg border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between bg-slate-50 px-4 py-3 text-left"
                  onClick={() =>
                    setOpenArticleId(open ? undefined : article.articleId)
                  }
                >
                  <span className="font-semibold">
                    {articleNumber}. {article.title} ({article.articleId})
                  </span>
                  <IconChevronDown
                    className={cn("transition", open ? "rotate-180" : "")}
                    size={16}
                  />
                </button>
                {open && (
                  <div className="space-y-2 p-3">
                    {SECTION_KEYS.map((section) => {
                      const sectionOpen =
                        openSections[article.articleId] === section.id;
                      return (
                        <div key={section.id} className="rounded border">
                          <button
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
                            onClick={() =>
                              setOpenSections((current) => ({
                                ...current,
                                [article.articleId]: sectionOpen ? "" : section.id,
                              }))
                            }
                          >
                            {section.label}
                            <IconChevronDown
                              size={14}
                              className={cn(sectionOpen ? "rotate-180" : "")}
                            />
                          </button>
                          {sectionOpen && (
                            <div className="border-t p-3">
                              <ArticleSection
                                article={article}
                                section={section.id}
                                onOpenLcd={(lcdId) => void openLcd(lcdId)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default McdCoverageTab;
